import { evaluateFormula } from "../helpers/evaluateFormula";
import isNumber from "../helpers/isNumber";
import { evaluate } from "../helpers/securedMathjs";
import { DisplayCondition, MathScopes } from "../types/genericTypes";
import type {
  ActionErrorSource,
  CalculatedAction,
  ImpactFormula,
  RawCalculableAction,
  RawRecommendedAction,
  WholeAction,
} from "../types/planTypes";

export const GRANULAR_MULTIPLIER = "GRANULAR_MULTIPLIER";
export type ActionsBuilder = {
  calculateApplicableActionsImpact: (
    actions: RawRecommendedAction[],
  ) => CalculatedAction[];
  prepareApplicableActions: (
    actions: RawRecommendedAction[],
  ) => RawRecommendedAction[];
  parseRawActionToCalculatedAction: (
    actions: RawCalculableAction,
  ) => CalculatedAction;
  buildWholeActionFromRawAction: (
    actions: RawCalculableAction[],
  ) => WholeAction[];
  buildWholeActionByJoin: (
    calculatedAction: CalculatedAction[],
    actions: RawCalculableAction[],
  ) => WholeAction[];
};

type ActionImpact = {
  value: number;
  error: boolean;
  errorSource?: ActionErrorSource;
  errorMessage?: string;
  evaluatedFormula?: string;
};

const matchCondition = (
  fullMathScope: MathScopes,
  condition: DisplayCondition,
): boolean => {
  const { operator, variableName, value } = condition;
  const isValueNumeric = isNumber(value);

  try {
    const numberValue = isValueNumeric
      ? +value
      : evaluate(value as string, fullMathScope);
    return evaluateFormula(fullMathScope, variableName, operator, numberValue);
  } catch (error) {
    return false;
  }
};
const calculateGranularImpact = (
  fullMathScope: MathScopes,
  impactFormulas: ImpactFormula[],
  optionValue: string,
) => {
  const mathScope = {
    ...fullMathScope,
    [GRANULAR_MULTIPLIER]: parseFloat(optionValue),
  };
  return calculateImpact(mathScope, impactFormulas);
};
const calculateMaximumGranularImpact = (
  fullMathScope: MathScopes,
  impactFormulas: ImpactFormula[],
  granularOptions: { value: string }[],
): ActionImpact => {
  let maxReduction = 0;
  let errorInCalculation = false;
  let errorSource: ActionErrorSource | undefined;
  let errorMessage: string | undefined;
  let evaluatedFormula: string | undefined;
  granularOptions.forEach((option) => {
    const impact = calculateGranularImpact(
      fullMathScope,
      impactFormulas,
      option.value,
    );
    if (impact.error) {
      errorInCalculation = true;
      if (!errorMessage) {
        errorSource = impact.errorSource;
        errorMessage = impact.errorMessage;
        evaluatedFormula = impact.evaluatedFormula;
      }
    }
    maxReduction = maxReduction < impact.value ? impact.value : maxReduction;
  });
  return {
    value: maxReduction,
    error: errorInCalculation,
    ...(errorSource ? { errorSource } : {}),
    ...(errorMessage ? { errorMessage } : {}),
    ...(evaluatedFormula ? { evaluatedFormula } : {}),
  };
};

export const calculateImpact = (
  fullMathScope: MathScopes,
  impactFormulas: ImpactFormula[],
): ActionImpact => {
  if (!Array.isArray(impactFormulas)) {
    return { value: 0, error: false };
  }
  const matchingFormula = impactFormulas.find((formula) =>
    formula.conditions.every((condition) =>
      matchCondition(fullMathScope, condition),
    ),
  );
  if (!matchingFormula) {
    return { value: 0, error: false };
  }
  try {
    const value = evaluate(matchingFormula.formula, fullMathScope);
    return { value, error: false };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error("Error evaluating impact formula:", {
      errorMessage,
      formula: matchingFormula.formula,
      error,
    });
    return {
      value: 0,
      error: true,
      errorSource: "impactFormula",
      errorMessage,
      evaluatedFormula: matchingFormula.formula,
    };
  }
};

const joinCalculatedActionWithRawAction = (
  calculated: CalculatedAction,
  raw: RawCalculableAction,
): WholeAction => ({
  links: raw.links,
  granularOptions: raw.granularOptions,
  help: raw.help,
  displayCondition: raw.displayCondition,
  richTextDescription: raw.richTextDescription,
  localizedRichTextDescription: raw.localizedRichTextDescription,
  richTextHelp: raw.richTextHelp,
  localizedRichTextHelp: raw.localizedRichTextHelp,
  ...calculated,
});

export const createActionsBuilder = (
  actionsVersion: string,
  fullMathScope: MathScopes,
  totalFootprint: number,
): ActionsBuilder => {
  return {
    prepareApplicableActions: (actions: RawCalculableAction[]) => {
      return actions.filter((action) => {
        return (
          !action.displayCondition ||
          action.displayCondition.every((condition) =>
            matchCondition(fullMathScope, condition),
          )
        );
      });
    },
    calculateApplicableActionsImpact(
      actions: RawCalculableAction[],
    ): CalculatedAction[] {
      const failedActions: { actionId: string; reason: unknown }[] = [];
      const calculatedActions: CalculatedAction[] = actions.reduce(
        (aggregator, action) => {
          try {
            const calculatedAction =
              this.parseRawActionToCalculatedAction(action);
            aggregator.push(calculatedAction);
            return aggregator;
          } catch (error: unknown) {
            failedActions.push({
              actionId: action.id,
              reason: error instanceof Error ? error.message : "Unknown",
            });
            return aggregator;
          }
        },
        [] as CalculatedAction[],
      );

      if (failedActions.length) {
        // Complete a structured log entry.
        const logEntry = Object.assign({
          severity: "WARNING",
          message: `Calculation of some actions failed. List of failed actions`,
          failedActions,
          fullMathScope,
          component: "actionCalculator",
        });
        console.log(JSON.stringify(logEntry));
      }

      return calculatedActions;
    },
    parseRawActionToCalculatedAction(
      action: RawCalculableAction,
    ): CalculatedAction {
      const selectedGranularOption = action.selectedGranularOption
        ? action.granularOptions?.find(
            ({ globalName, option }) =>
              globalName === action.selectedGranularOption ||
              /* Fallback for old granular actions that used the option 
               text before globalName was introduced */
              option === action.selectedGranularOption,
          )
        : undefined;
      let calculatedImpact: ActionImpact = { value: 0, error: false };
      if (!action.granularOptions || action.granularOptions.length === 0) {
        calculatedImpact = calculateImpact(
          fullMathScope,
          action.impactFormulas,
        );
      } else if (selectedGranularOption) {
        calculatedImpact = calculateGranularImpact(
          fullMathScope,
          action.impactFormulas,
          selectedGranularOption.value,
        );
      } else {
        calculatedImpact = calculateMaximumGranularImpact(
          fullMathScope,
          action.impactFormulas,
          action.granularOptions ?? [],
        );
      }
      return {
        state: "new",
        actionsVersion: actionsVersion,
        calculatedImpact: calculatedImpact.value,
        calculateImpactError: calculatedImpact.error,
        ...(calculatedImpact.errorSource
          ? { calculateImpactErrorSource: calculatedImpact.errorSource }
          : {}),
        ...(calculatedImpact.errorMessage
          ? { calculateImpactErrorMessage: calculatedImpact.errorMessage }
          : {}),
        ...(calculatedImpact.evaluatedFormula
          ? { calculateImpactErrorFormula: calculatedImpact.evaluatedFormula }
          : {}),
        percentReduction: (calculatedImpact.value / totalFootprint) * 100,
        id: action.id,
        skipIdsIfSelected: action.skipIdsIfSelected,
        impactFormulas: action.impactFormulas,
        tags: action.tags,
        category: action.category,
        title: action.title,
        type: action.type,
        sortKey: action.sortKey,
      };
    },
    buildWholeActionFromRawAction(
      actions: RawCalculableAction[],
    ): WholeAction[] {
      return actions.map((action) => {
        const calculatedAction = this.parseRawActionToCalculatedAction(action);
        return joinCalculatedActionWithRawAction(calculatedAction, action);
      });
    },
    buildWholeActionByJoin(
      calculatedActions: CalculatedAction[],
      rawActions: RawCalculableAction[],
    ) {
      const joinedActions: WholeAction[] = [];
      calculatedActions.forEach((calculatedAction) => {
        const matchedAction = rawActions.find(
          (rawAction) => rawAction.id === calculatedAction.id,
        );
        if (!matchedAction) return;
        joinedActions.push(
          joinCalculatedActionWithRawAction(calculatedAction, matchedAction),
        );
      });
      return joinedActions;
    },
  };
};
